/**
 * BROWSER STORAGE THAT CANNOT TAKE THE PAGE DOWN.
 *
 * ── The report ──────────────────────────────────────────────────────────────
 * Submitting a prompt for a NEW build put the user on the full-page error
 * screen instead of the builder:
 *
 *   Failed to execute 'setItem' on 'Storage': Setting the value of
 *   'panda:growth-mode:9228999b-06b4-4b90-8b56-1d259da5928b' exceeded the quota.
 *
 * The write that threw stores the string "false". It is a preference for a
 * panel the user had not opened, on a project that did not exist a second
 * earlier, and it ran unguarded inside a `useEffect` — so the throw reached the
 * app error boundary and replaced the whole workspace with "Something went
 * wrong". A four-byte convenience took down the product's front door.
 *
 * ── Why a NEW build, specifically ───────────────────────────────────────────
 * Overwriting a key that already exists does not grow the origin's footprint,
 * so it is the FIRST write of a brand-new key that fails once the quota is
 * full — and every new build mints a new project id, which means a new key.
 * That is why this reproduces on the new-build journey and not on reopening an
 * existing project. (The browser's exact accounting is its own; what this
 * module guarantees is that neither case can throw.)
 *
 * ── Why the quota was full ──────────────────────────────────────────────────
 * Not this key. Panda writes about a dozen kinds of per-project entry and
 * evicts none of them, so the origin's footprint grows with every project the
 * user has ever opened and never shrinks. The biggest by far is the chat
 * presentation snapshot (`panda:chat-restore:<projectId>`), which is uncapped
 * and carries each message's full text plus its build recap. Nothing in the
 * repo bounded it and nothing ever removed one.
 *
 * ── What this module does ───────────────────────────────────────────────────
 * Two things, and deliberately in this order:
 *
 *   1. NEVER THROW. Every helper here returns a value instead — storage being
 *      full, disabled or absent is an ordinary condition (private windows,
 *      hardened browsers, an iOS in-app webview), not an exception. A write
 *      that fails costs a convenience; it may never cost the page.
 *   2. MAKE ROOM, ONCE, AND ONLY IN OUR OWN CACHES. On a quota failure it
 *      releases entries Panda can rebuild from the server, largest first, and
 *      retries. It will not touch a key outside the reclaimable list below —
 *      which is an ALLOWLIST, because the same origin also holds the Supabase
 *      session and clearing that would sign the user out to save a preference.
 *
 * Nothing here decides anything about the user. It is plumbing.
 */

/** The prefix Panda owns. Nothing outside it is ever read, moved or removed. */
const PANDA_PREFIX = "panda";

/**
 * Entries Panda can rebuild, in the order they are given up.
 *
 * TIER 1 — pure caches of server-authoritative data. Losing one costs a
 * re-render from what the server already holds, and nothing else. The chat
 * snapshot is first because it is both the most expendable and, by some
 * distance, the largest.
 *
 * TIER 2 — small per-project conveniences. Losing one re-shows a notice the
 * user dismissed, or forgets which panel a project was last left on. Given up
 * only after every tier-1 entry has gone, and never for the project being
 * written to.
 *
 * A prefix that is NOT here is protected by omission. That covers the entries
 * this module must never trade away — an unsent prompt, a queued follow-up, a
 * checkout in flight, an auth resume, attribution — and it covers every key
 * belonging to anything that is not Panda.
 */
const RECLAIMABLE: readonly { prefix: string; tier: 1 | 2 }[] = [
  // Restored from the messages table on load; the snapshot only carries how
  // they were presented.
  { prefix: "panda:chat-restore:", tier: 1 },
  // Suggestion chips + readiness, recomputed on demand.
  { prefix: "panda:chat-suggest:", tier: 1 },
  // A hint about which pages a viewer has seen. Cosmetic.
  { prefix: "panda:project-history:", tier: 1 },
  { prefix: "panda:project-template:", tier: 2 },
  { prefix: "panda:growth-mode:", tier: 2 },
  { prefix: "panda:growth-external-url:", tier: 2 },
  { prefix: "panda:load-origin:", tier: 2 },
  { prefix: "panda:return-feedback:", tier: 2 },
  { prefix: "panda:demo-limits-view:", tier: 2 },
  { prefix: "panda:repair-session:", tier: 2 },
  { prefix: "panda:onboarding-shown:", tier: 2 },
  { prefix: "panda:notification-banner-dismissed:", tier: 2 },
];

export type StorageKind = "local" | "session";

/** The store, or null when the browser has none to give. Reading `localStorage`
 *  itself throws in some hardened configurations, which is why even this is in
 *  a try. */
function store(kind: StorageKind): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    const s = kind === "local" ? window.localStorage : window.sessionStorage;
    // A present-but-unusable store (some in-app webviews) is caught by the
    // callers' own try blocks; this only proves the property is reachable.
    return s ?? null;
  } catch {
    return null;
  }
}

/** Every key currently in the store, or an empty list. Snapshotted before any
 *  removal, because removing while indexing by position skips entries. */
function keysOf(s: Storage): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k) out.push(k);
    }
  } catch {
    return out;
  }
  return out;
}

/**
 * Is this the quota, rather than some other failure?
 *
 * Browsers disagree on how to say so — a named `QuotaExceededError`, Firefox's
 * `NS_ERROR_DOM_QUOTA_REACHED`, and legacy numeric codes — and Safari in a
 * private window throws a plain error whose message is the only clue. All of
 * them mean the same thing here, and a wrong answer only costs one pointless
 * reclamation pass, so this is deliberately generous.
 */
function isQuotaError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; code?: number; message?: string };
  if (e.name === "QuotaExceededError") return true;
  if (e.name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  if (e.code === 22 || e.code === 1014) return true;
  return /quota/i.test(e.message ?? "");
}

/** The project id a per-project key belongs to, or null. Used only to keep the
 *  project being written to out of its own reclamation. */
function projectIdOf(key: string): string | null {
  for (const { prefix } of RECLAIMABLE) {
    if (key.startsWith(prefix)) {
      const rest = key.slice(prefix.length);
      return rest || null;
    }
  }
  return null;
}

interface Candidate {
  key: string;
  tier: 1 | 2;
  /** Characters held by this entry — key plus value. An approximation of the
   *  browser's own accounting, and good enough to release the biggest first. */
  size: number;
}

/**
 * Release regenerable Panda entries until `need` characters have been freed or
 * the candidates run out. Returns how much was released.
 *
 * ORDERED BY TIER, THEN BY SIZE. Tier before size so a large snapshot always
 * goes before a small preference, and size within a tier so the fewest entries
 * are given up for the space needed. The project being written to is skipped
 * entirely: it is the one the user is looking at.
 */
function reclaim(s: Storage, need: number, keepProjectId: string | null): number {
  const candidates: Candidate[] = [];
  for (const key of keysOf(s)) {
    if (!key.startsWith(PANDA_PREFIX)) continue;
    const match = RECLAIMABLE.find((r) => key.startsWith(r.prefix));
    if (!match) continue;
    if (keepProjectId && projectIdOf(key) === keepProjectId) continue;
    let value: string | null = null;
    try {
      value = s.getItem(key);
    } catch {
      continue;
    }
    candidates.push({
      key,
      tier: match.tier,
      size: key.length + (value?.length ?? 0),
    });
  }
  candidates.sort((a, b) => a.tier - b.tier || b.size - a.size);

  let freed = 0;
  for (const c of candidates) {
    if (freed >= need) break;
    try {
      s.removeItem(c.key);
      freed += c.size;
    } catch {
      // A key that will not go is not worth failing over; try the next.
    }
  }
  return freed;
}

export interface SetResult {
  ok: boolean;
  /** Why a write did not land. `null` on success. */
  reason: "unavailable" | "quota" | "error" | null;
  /** Characters released to make room, when a reclamation pass ran. */
  reclaimed: number;
}

/**
 * Write, and say what happened. NEVER THROWS.
 *
 * On a quota failure it releases regenerable entries worth several times the
 * value being written — enough that a page which writes a handful of small
 * keys in a row does not pay for a reclamation pass on each one — and retries
 * exactly once. A second failure is reported, not raised: the caller's state is
 * already correct in memory, and the only thing lost is that it will not
 * survive a reload.
 */
export function safeSetItem(
  key: string,
  value: string,
  opts?: { kind?: StorageKind },
): SetResult {
  const s = store(opts?.kind ?? "local");
  if (!s) return { ok: false, reason: "unavailable", reclaimed: 0 };
  try {
    s.setItem(key, value);
    return { ok: true, reason: null, reclaimed: 0 };
  } catch (err) {
    if (!isQuotaError(err)) {
      return { ok: false, reason: "error", reclaimed: 0 };
    }
    // Ask for materially more than this write needs. Reclaiming exactly enough
    // leaves the store full again, so the next small write repeats the whole
    // pass — and on the new-build journey several land within a second.
    const need = Math.max((key.length + value.length) * 8, 64_000);
    const reclaimed = reclaim(s, need, projectIdOf(key));
    /**
     * THE ONE SIGNAL THIS MODULE EMITS, and the one the report had to be filed
     * to discover: nothing anywhere said the store was full until a user hit
     * the error boundary.
     *
     * A console line rather than an analytics call, deliberately — this module
     * is imported by the home page, the pricing page and the workspace, and a
     * dependency on the tracker would drag the tracker into all of them. Both
     * Sentry and PostHog record console output as breadcrumbs, so a crash or a
     * session replay now carries "we were reclaiming storage" ahead of it.
     */
    console.warn(
      `[storage] quota reached writing ${key} — released ${reclaimed} chars of regenerable cache`,
    );
    if (reclaimed > 0) {
      try {
        s.setItem(key, value);
        return { ok: true, reason: null, reclaimed };
      } catch {
        // Fall through — still full, or full again.
      }
    }
    return { ok: false, reason: "quota", reclaimed };
  }
}

/** Read, or null. NEVER THROWS. */
export function safeGetItem(
  key: string,
  opts?: { kind?: StorageKind },
): string | null {
  const s = store(opts?.kind ?? "local");
  if (!s) return null;
  try {
    return s.getItem(key);
  } catch {
    return null;
  }
}

/** Remove. NEVER THROWS. Reports whether the store was reachable at all. */
export function safeRemoveItem(
  key: string,
  opts?: { kind?: StorageKind },
): boolean {
  const s = store(opts?.kind ?? "local");
  if (!s) return false;
  try {
    s.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Write JSON, trimming the value down before giving up.
 *
 * `shrink` is handed the value and asked for a smaller one — typically the same
 * record with its oldest entries dropped. It is called only after a reclamation
 * pass has already failed, so an ordinary write never pays for it, and a store
 * that is full of OTHER origins' data still keeps whatever fits.
 *
 * This is what bounds the entry that filled the quota in the first place: the
 * chat snapshot is written through here and gives up its oldest cards rather
 * than failing whole.
 */
export function safeSetJson<T>(
  key: string,
  value: T,
  opts?: { kind?: StorageKind; shrink?: (value: T) => T | null },
): SetResult {
  let current: T | null = value;
  let lastReclaimed = 0;
  // Three attempts: as-is, then two progressively smaller. A fourth would be
  // trading real content for a cache nobody is waiting on.
  for (let attempt = 0; attempt < 3 && current !== null; attempt += 1) {
    let serialized: string;
    try {
      serialized = JSON.stringify(current);
    } catch {
      return { ok: false, reason: "error", reclaimed: lastReclaimed };
    }
    const result = safeSetItem(key, serialized, opts);
    lastReclaimed = Math.max(lastReclaimed, result.reclaimed);
    if (result.ok) return { ...result, reclaimed: lastReclaimed };
    if (result.reason !== "quota" || !opts?.shrink) {
      return { ...result, reclaimed: lastReclaimed };
    }
    current = opts.shrink(current);
  }
  return { ok: false, reason: "quota", reclaimed: lastReclaimed };
}
